import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Typography, Button, Alert, CircularProgress, Chip, Tooltip, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TablePagination, Dialog, DialogTitle, DialogContent, DialogActions,
  Avatar, Divider, Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BadgeIcon from "@mui/icons-material/Badge";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import CakeIcon from "@mui/icons-material/Cake";
import EventIcon from "@mui/icons-material/Event";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import NotesIcon from "@mui/icons-material/Notes";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import EditarEmpleadoDialog from "../components/EditarEmpleadoDialog";
import ModificarContratoDialog from "../components/ModificarContratoDialog";
import EditarHistorialDialog from "../components/EditarHistorialDialog";
import EditarLicenciaDialog from "../components/EditarLicenciaDialog";
import { useAuth } from "../../../context/AuthContext";
import EmitirMemoDialog from "../components/EmitirMemoDialog";

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.warning.dark : theme.palette.warning.light,
  color: theme.palette.mode === "dark" ? "#fff3e0" : "#5c3300",
}));

const TIPOS_CONTRATO = { 1: "Contrato", 2: "Eventual" };

// Campo de la card "Datos del Empleado": ícono + etiqueta chica + valor, en vez de
// "**Label:** valor" en una sola línea — mejor jerarquía visual y escaneable.
const Campo = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Box sx={{ color: "warning.main", mt: "1px", display: "flex" }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Box>
  </Stack>
);

// per_empleado/per_contrato son datos legacy con años de antigüedad — una fecha
// vacía se guardaba como '0000-00-00', que el driver devuelve como 30/11/1899.
// Se trata como "sin dato" (año < 1950, ninguna fecha real de RRHH cae ahí).
const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1950) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatMonto = (value) =>
  `Bs ${Number(value || 0).toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Mismo patrón que MisSolicitudesPermisoPage.jsx: listado paginado contra un
// endpoint GET con {page, limit} -> {data, total}, reusado acá para las 3 listas
// (Contratos, Ganados, Licencias) además de Memorandums.
const useListadoPaginado = (url) => {
  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const cargar = useCallback((targetPage, targetRowsPerPage) => {
    if (!url) return;
    setLoading(true);
    setError("");
    axios.get(url, { params: { page: targetPage + 1, limit: targetRowsPerPage } })
      .then(({ data }) => {
        setLista(data?.data || []);
        setTotal(Number(data?.total || 0));
      })
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    cargar(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, url]);

  return { lista, total, loading, error, page, rowsPerPage, setPage, setRowsPerPage, reset: () => cargar(page, rowsPerPage) };
};

const Paginacion = ({ total, page, rowsPerPage, onPageChange, onRowsPerPageChange }) => (
  <TablePagination
    component="div"
    count={total}
    page={page}
    rowsPerPage={rowsPerPage}
    rowsPerPageOptions={[10, 25, 50]}
    labelRowsPerPage="Filas por página"
    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
    onPageChange={(_, newPage) => onPageChange(newPage)}
    onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
  />
);

const abrirPdfEnPestania = async (url, setError) => {
  const pdfWindow = window.open("", "_blank");
  if (pdfWindow) pdfWindow.opener = null;
  try {
    const { data } = await axios.get(url, { responseType: "blob" });
    const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    if (pdfWindow) {
      pdfWindow.location.replace(pdfUrl);
    } else {
      window.open(pdfUrl, "_blank");
    }
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
  } catch (e) {
    if (pdfWindow) pdfWindow.close();
    setError(e?.response?.data?.error || "No se pudo generar el PDF.");
  }
};

// Quién puede editar las vacaciones y permisos ya emitidos: el rol admin, o el rol conta con el subrol cont (el servidor lo comprueba otra vez)
const puedeEditarLicencias = (user) => {
  const role = String(user?.role || "").trim().toLowerCase();
  const sub = String(user?.sub_rol || user?.subRol || "").trim().toLowerCase();
  return role === "admin" || (role === "conta" && sub === "cont");
};

const AdministracionPersonalEmpleadoPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const puedeEditarLic = puedeEditarLicencias(user);
  const [editarLicenciaRow, setEditarLicenciaRow] = useState(null);
  const [empleado, setEmpleado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saldo, setSaldo] = useState(null);

  const [editarEmpleadoOpen, setEditarEmpleadoOpen] = useState(false);
  const [modificarContratoOpen, setModificarContratoOpen] = useState(false);
  const [finalizarContratoOpen, setFinalizarContratoOpen] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [editarHistorialRow, setEditarHistorialRow] = useState(null);
  const [eliminarHistorialRow, setEliminarHistorialRow] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [emitirMemoOpen, setEmitirMemoOpen] = useState(false);

  const contratos = useListadoPaginado(id ? `/api/administracion-personal/empleado/${id}/contratos` : null);
  const ganados = useListadoPaginado(id ? `/api/permisos/empleado/${id}/historial-completo` : null);
  const licencias = useListadoPaginado(id ? `/api/permisos/empleado/${id}/licencias-completo` : null);
  const memos = useListadoPaginado(id ? `/api/administracion-personal/empleado/${id}/memos` : null);

  const cargarEmpleado = useCallback(() => {
    setLoading(true);
    setError("");
    axios.get(`/api/administracion-personal/empleado/${id}`)
      .then(({ data }) => setEmpleado(data?.data))
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar el empleado."))
      .finally(() => setLoading(false));
  }, [id]);

  const cargarSaldo = useCallback(() => {
    axios.get(`/api/permisos/empleado/${id}/disponibilidad`)
      .then(({ data }) => setSaldo(data?.data?.saldo ?? null))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    cargarEmpleado();
    cargarSaldo();
  }, [cargarEmpleado, cargarSaldo]);

  const handleFinalizarContrato = async () => {
    setFinalizando(true);
    try {
      await axios.patch(`/api/administracion-personal/empleado/${id}/finalizar-contrato`);
      setFinalizarContratoOpen(false);
      cargarEmpleado();
      contratos.reset();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al finalizar el contrato.");
    } finally {
      setFinalizando(false);
    }
  };

  const handleEliminarHistorial = async () => {
    setEliminando(true);
    try {
      await axios.delete(`/api/permisos/historial/${eliminarHistorialRow.id}`);
      setEliminarHistorialRow(null);
      ganados.reset();
      cargarSaldo();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al eliminar el registro.");
    } finally {
      setEliminando(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (!empleado) {
    return (
      <DashboardLayout>
        <PageHeader title="Historial" backTo="/administracion-personal" />
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error || "No se pudo cargar el empleado."}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  const nombreCompleto = [empleado.nombre, empleado.paterno, empleado.materno].filter(Boolean).join(" ");

  return (
    <DashboardLayout>
      <PageHeader title={nombreCompleto} subtitle={empleado.contrato?.cargo || "Historial"} backTo="/administracion-personal" />
      <Box sx={{ p: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {/* Datos del empleado */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: "warning.main", color: "#5c3300", fontWeight: 800, fontSize: "1.4rem" }}>
              {(empleado.nombre?.[0] || "?").toUpperCase()}{(empleado.paterno?.[0] || "").toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{nombreCompleto}</Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
                {empleado.cargo && <Chip size="small" label={empleado.cargo} sx={{ height: 20, fontSize: "0.68rem" }} />}
                {empleado.departamento && <Chip size="small" variant="outlined" label={empleado.departamento} sx={{ height: 20, fontSize: "0.68rem" }} />}
                {empleado.empresa && <Chip size="small" variant="outlined" label={empleado.empresa} sx={{ height: 20, fontSize: "0.68rem" }} />}
              </Stack>
            </Box>
            <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />} onClick={() => setEditarEmpleadoOpen(true)}>
              Editar
            </Button>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
            <Campo icon={<BadgeIcon fontSize="small" />} label="Carnet de Identidad" value={empleado.ci ? `${empleado.ci} ${empleado.procedencia_ci || ""}`.trim() : null} />
            <Campo icon={<SchoolIcon fontSize="small" />} label="Título" value={empleado.titulo} />
            <Campo icon={<EmailIcon fontSize="small" />} label="Correo" value={empleado.correo} />
            <Campo icon={<PhoneIphoneIcon fontSize="small" />} label="Celular" value={empleado.celular} />

            <Campo icon={<PhoneIcon fontSize="small" />} label="Teléfono" value={empleado.telefono} />
            <Campo icon={<LocationOnIcon fontSize="small" />} label="Dirección" value={empleado.direccion} />
            <Campo icon={<WorkIcon fontSize="small" />} label="Cargo" value={empleado.cargo} />
            <Campo icon={<CakeIcon fontSize="small" />} label="Fecha Cumpleaños" value={formatFecha(empleado.fecha_cumple)} />

            <Campo icon={<EventIcon fontSize="small" />} label="Fecha de Inicio" value={formatFecha(empleado.contrato?.fecha_ini)} />
            <Campo icon={<EventIcon fontSize="small" />} label="Fecha Pasantía" value={formatFecha(empleado.fecha_pas)} />
            <Campo icon={<EventIcon fontSize="small" />} label="Fecha de Contrato" value={formatFecha(empleado.fecha_con)} />
            <Campo icon={<EventIcon fontSize="small" />} label="Fecha de Afiliación" value={formatFecha(empleado.fecha_afi)} />

            <Campo icon={<AccountBalanceWalletIcon fontSize="small" />} label="Nro de Cuenta" value={empleado.nro_cuenta} />
            <Campo icon={<LocalHospitalIcon fontSize="small" />} label="Caja" value={empleado.caja} />
            <Campo icon={<AssignmentIndIcon fontSize="small" />} label="Código del Asegurado" value={empleado.codigo_asegurado} />
            <Campo icon={<FingerprintIcon fontSize="small" />} label="NUA" value={empleado.nua} />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", p: 1, borderRadius: 1, bgcolor: "action.hover" }}>
              <ContactPhoneIcon fontSize="small" sx={{ color: "text.secondary", mt: "1px" }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Persona de Contacto</Typography>
                <Typography variant="body2">{empleado.contacto || "-"}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", p: 1, borderRadius: 1, bgcolor: "action.hover" }}>
              <NotesIcon fontSize="small" sx={{ color: "text.secondary", mt: "1px" }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Observaciones</Typography>
                <Typography variant="body2">{empleado.observaciones || "-"}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Historial de Contratos */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Historial de Contratos</Typography>
          {empleado.contrato ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setModificarContratoOpen(true)}>
                Modificar Contrato
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={() => setFinalizarContratoOpen(true)}>
                Finalizar Contrato
              </Button>
            </Box>
          ) : (
            <Tooltip title="Próximamente">
              <span>
                <Button size="small" variant="outlined" disabled>Registrar Contrato</Button>
              </span>
            </Tooltip>
          )}
        </Box>
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <TableContainer>
            <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
              <TableHead>
                <TableRow>
                  <Th>Ciudad</Th>
                  <Th>Fecha</Th>
                  <Th>Fecha Fin</Th>
                  <Th>Tipo</Th>
                  <Th>Cargo</Th>
                  <Th align="right">Sueldo Básico</Th>
                  <Th align="right">Bono</Th>
                  <Th align="center">Estado</Th>
                  <Th>Observación</Th>
                  <Th>Reg.</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {contratos.loading ? (
                  <TableRow><TableCell colSpan={10} align="center"><CircularProgress size={20} /></TableCell></TableRow>
                ) : contratos.lista.length === 0 ? (
                  <TableRow><TableCell colSpan={10}><Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>Sin contratos registrados.</Typography></TableCell></TableRow>
                ) : contratos.lista.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.ciudad}</TableCell>
                    <TableCell>{formatFecha(r.fecha_mostrar)}</TableCell>
                    <TableCell>{formatFecha(r.fecha_fin)}</TableCell>
                    <TableCell>{TIPOS_CONTRATO[r.tipo] || r.tipo}</TableCell>
                    <TableCell>{r.cargo}</TableCell>
                    <TableCell align="right">{formatMonto(r.sueldo_basico)}</TableCell>
                    <TableCell align="right">{formatMonto(r.sueldo)}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={r.estado} color={r.estado === "Vigente" ? "success" : "default"} sx={{ height: 19, fontSize: "0.68rem" }} />
                    </TableCell>
                    <TableCell>{r.obs || "-"}</TableCell>
                    <TableCell>{r.usu}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paginacion
            total={contratos.total} page={contratos.page} rowsPerPage={contratos.rowsPerPage}
            onPageChange={contratos.setPage}
            onRowsPerPageChange={(n) => { contratos.setRowsPerPage(n); contratos.setPage(0); }}
          />
        </Paper>

        {/* Ganados */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Ganados</Typography>
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <TableContainer>
            <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
              <TableHead>
                <TableRow>
                  <Th>Fecha de Registro</Th>
                  <Th align="center">Gestión</Th>
                  <Th align="center">Días</Th>
                  <Th>Observación</Th>
                  <Th align="center">Acción</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {ganados.loading ? (
                  <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={20} /></TableCell></TableRow>
                ) : ganados.lista.length === 0 ? (
                  <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>Sin historial de disponibilidad.</Typography></TableCell></TableRow>
                ) : ganados.lista.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{formatFecha(r.fecha)}</TableCell>
                    <TableCell align="center">{r.year}</TableCell>
                    <TableCell align="center">{r.cantidad}</TableCell>
                    <TableCell>{r.observacion || "-"}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => setEditarHistorialRow(r)} title="Editar">
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setEliminarHistorialRow(r)} title="Eliminar">
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paginacion
            total={ganados.total} page={ganados.page} rowsPerPage={ganados.rowsPerPage}
            onPageChange={ganados.setPage}
            onRowsPerPageChange={(n) => { ganados.setRowsPerPage(n); ganados.setPage(0); }}
          />
        </Paper>

        {/* Vacación o Permisos */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Vacación o Permisos</Typography>
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <TableContainer>
            <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
              <TableHead>
                <TableRow>
                  <Th>Fecha de Petición</Th>
                  <Th>Razón</Th>
                  <Th>Fecha Inicio</Th>
                  <Th>Fecha Finalización</Th>
                  <Th align="center">Días</Th>
                  <Th>Observación</Th>
                  <Th align="center">Imprimir</Th>
                  {puedeEditarLic && <Th align="center">Editar</Th>}
                </TableRow>
              </TableHead>
              <TableBody>
                {licencias.loading ? (
                  <TableRow><TableCell colSpan={puedeEditarLic ? 8 : 7} align="center"><CircularProgress size={20} /></TableCell></TableRow>
                ) : licencias.lista.length === 0 ? (
                  <TableRow><TableCell colSpan={puedeEditarLic ? 8 : 7}><Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>Sin permisos emitidos.</Typography></TableCell></TableRow>
                ) : licencias.lista.map((r, idx) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{formatFecha(r.fecha_peticion)}</TableCell>
                    <TableCell>{r.razon}</TableCell>
                    <TableCell>{formatFecha(r.fecha_ini)}</TableCell>
                    <TableCell>{formatFecha(r.fecha_fin)}</TableCell>
                    <TableCell align="center">{r.cantidad}</TableCell>
                    <TableCell>{r.observacion || "-"}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => abrirPdfEnPestania(`/api/permisos/licencia/${r.id}/papeleta.pdf`, setError)} title="Imprimir papeleta">
                        <PrintIcon fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                    {puedeEditarLic && (
                      <TableCell align="center">
                        {/* La lista va de la más reciente a la más antigua: solo las 3 primeras de la primera página se pueden editar */}
                        {licencias.page === 0 && idx < 3 ? (
                          <IconButton size="small" onClick={() => setEditarLicenciaRow(r)} title="Editar vacación o permiso">
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        ) : (
                          <Typography component="span" sx={{ color: "text.disabled" }} title="Solo se pueden editar las 3 últimas">—</Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paginacion
            total={licencias.total} page={licencias.page} rowsPerPage={licencias.rowsPerPage}
            onPageChange={licencias.setPage}
            onRowsPerPageChange={(n) => { licencias.setRowsPerPage(n); licencias.setPage(0); }}
          />
        </Paper>

        {saldo !== null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Cantidad de Días Pendientes de Vacación: <strong>{saldo}</strong>
          </Alert>
        )}

        {/* Memorandums */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Memorandums</Typography>
          <Button size="small" variant="outlined" onClick={() => setEmitirMemoOpen(true)}>
            Emitir Memo
          </Button>
        </Box>
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
              <TableHead>
                <TableRow>
                  <Th>Fecha de Memo</Th>
                  <Th>Asunto</Th>
                  <Th>Detalle</Th>
                  <Th align="center">Ver</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {memos.loading ? (
                  <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={20} /></TableCell></TableRow>
                ) : memos.lista.length === 0 ? (
                  <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>Sin memos emitidos.</Typography></TableCell></TableRow>
                ) : memos.lista.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{formatFecha(r.fecha)}</TableCell>
                    <TableCell>{r.asunto}</TableCell>
                    <TableCell sx={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.detalle}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => abrirPdfEnPestania(`/api/administracion-personal/memo/${r.id}/pdf`, setError)} title="Ver memo">
                        <VisibilityIcon fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paginacion
            total={memos.total} page={memos.page} rowsPerPage={memos.rowsPerPage}
            onPageChange={memos.setPage}
            onRowsPerPageChange={(n) => { memos.setRowsPerPage(n); memos.setPage(0); }}
          />
        </Paper>
      </Box>

      <EditarEmpleadoDialog
        open={editarEmpleadoOpen}
        empleado={empleado}
        onClose={() => setEditarEmpleadoOpen(false)}
        onGuardado={() => { setEditarEmpleadoOpen(false); cargarEmpleado(); }}
      />

      {empleado.contrato && (
        <ModificarContratoDialog
          open={modificarContratoOpen}
          empleadoId={id}
          contrato={empleado.contrato}
          onClose={() => setModificarContratoOpen(false)}
          onGuardado={() => { setModificarContratoOpen(false); cargarEmpleado(); contratos.reset(); }}
        />
      )}

      <EditarLicenciaDialog
        open={Boolean(editarLicenciaRow)}
        registro={editarLicenciaRow}
        onClose={() => setEditarLicenciaRow(null)}
        onGuardado={() => { setEditarLicenciaRow(null); licencias.reset(); cargarSaldo(); }}
      />

      <EditarHistorialDialog
        open={Boolean(editarHistorialRow)}
        registro={editarHistorialRow}
        onClose={() => setEditarHistorialRow(null)}
        onGuardado={() => { setEditarHistorialRow(null); ganados.reset(); cargarSaldo(); }}
      />

      <Dialog open={finalizarContratoOpen} onClose={() => (finalizando ? null : setFinalizarContratoOpen(false))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Finalizar Contrato</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">¿Realmente desea finalizar el contrato Vigente de {nombreCompleto}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizarContratoOpen(false)} disabled={finalizando}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleFinalizarContrato} disabled={finalizando}>
            {finalizando ? "Guardando..." : "Finalizar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(eliminarHistorialRow)} onClose={() => (eliminando ? null : setEliminarHistorialRow(null))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Eliminar registro</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            ¿Eliminar el registro de {eliminarHistorialRow?.cantidad} día(s) de la gestión {eliminarHistorialRow?.year}? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEliminarHistorialRow(null)} disabled={eliminando}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleEliminarHistorial} disabled={eliminando}>
            {eliminando ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <EmitirMemoDialog
        open={emitirMemoOpen}
        empleado={{ id, nombre: nombreCompleto }}
        onClose={() => setEmitirMemoOpen(false)}
        onEmitido={() => { setEmitirMemoOpen(false); memos.reset(); }}
      />
    </DashboardLayout>
  );
};

export default AdministracionPersonalEmpleadoPage;
